import axios from 'axios'

const BASE = import.meta.env.VITE_API_URL || '/api'
export const api = axios.create({ baseURL: BASE })
let _token = ''
export function setAuthToken(token: string) { _token = token }

export function errorMsg(e: any, fallback = 'Unknown error'): string {
  const d = e?.response?.data?.detail
  if (!d) return e?.message || fallback
  if (Array.isArray(d)) return d.map((x: any) => x?.msg ?? JSON.stringify(x)).join('; ')
  return String(d)
}
api.interceptors.request.use(config => {
  if (_token) config.headers['Authorization'] = `Bearer ${_token}`
  return config
})

export type ScheduleType = 'daily' | 'weekly' | 'monthly' | 'multiple'

export type Report = {
  report_id:             string
  name:                  string
  description:           string
  script_key:            string
  schedule_type:         ScheduleType
  schedule_times:        string[]
  schedule_days:         string
  schedule_day_of_month: number
  schedule_time:         string   // legacy
  email_list_key:        string
  active:                boolean
  created_by:            string
  created_at:            string
  updated_at:            string
}

export type Execution = {
  report_id:    string
  started_at:   string
  task_arn:     string
  triggered_by: string
  status:       'RUNNING' | 'SUCCESS' | 'FAILED' | 'UNKNOWN'
  exit_code?:   number
  ended_at?:    string
  duration_s?:  number
}

export const reportsApi = {
  list:       ()                                    => api.get<{ reports: Report[] }>('/reports'),
  create:     (data: Partial<Report>)               => api.post<Report>('/reports', data),
  update:     (id: string, data: Partial<Report>)   => api.put<Report>(`/reports/${id}`, data),
  delete:     (id: string)                          => api.delete(`/reports/${id}`),
  runNow:     (id: string)                          =>
    api.post<{ task_arn: string; report_id: string; started_at: string }>(`/reports/${id}/run`),
  executions: (id: string, limit = 10)              =>
    api.get<{ executions: Execution[] }>(`/reports/${id}/executions`, { params: { limit } }),
}

export const executionsApi = {
  status: (task_arn: string, report_id: string, started_at: string) =>
    api.get<{ status: string; exit_code?: number; duration_s?: number }>(
      '/executions/status', { params: { task_arn, report_id, started_at } }
    ),
}

export const filesApi = {
  list:   () => api.get<{ files: { key: string; size: number; last_modified: string }[] }>('/files'),
  upload: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/upload', fd)
  },
}

export const emailListApi = {
  list:   () => api.get<{ files: { key: string; last_modified: string }[] }>('/email-list'),
  upload: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return api.post('/email-list', fd)
  },
}

export type User = {
  email:    string
  name:     string
  role:     'admin' | 'user'
  added_by: string
  added_at: string
  active:   boolean
}

export const usersApi = {
  list:    ()                                            => api.get<{ users: User[] }>('/users'),
  add:     (data: { email: string; name: string; role: string }) => api.post<User>('/users', data),
  setRole: (email: string, role: string)                => api.put(`/users/${encodeURIComponent(email)}/role`, { role }),
  remove:  (email: string)                              => api.delete(`/users/${encodeURIComponent(email)}`),
  whoami:  ()                                           => api.get<{ email: string; name: string; role: string }>('/whoami'),
}

export type LogStream = {
  stream:   string
  task_id:  string
  first_ms: number | null
  last_ms:  number | null
  size:     number
}
export type LogEvent = { ts: number; msg: string }

export const logsApi = {
  groups: () => api.get<{ log_groups: string[] }>('/log-groups'),
  list:   (log_group?: string) =>
    api.get<{ streams: LogStream[]; log_group: string }>('/logs',
      log_group ? { params: { log_group } } : {}),
  events: (stream: string, log_group?: string) =>
    api.get<{ stream: string; events: LogEvent[] }>('/logs/events',
      { params: log_group ? { stream, log_group } : { stream } }),
}
